import { Button } from '@cian/ui-kit/button';
import type { FC } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';

import { AddressField } from './AddressField';
import { buildDeliveryAddress } from './addressRules';
import style from './FormStep.css';
import { FormResult } from './FormResult';
import { IFormData } from './FormStep.types';
import { selectInitialStickerCount, selectFormStatus, selectErrorCode, submitSuperhostRequest } from '../../../slices';
import { TDispatch } from '../../../types/redux';

const PERSONAL_DATA_CONSENT_URL = 'https://cdn.cian.site/frontend/info-assets/consents/superhost.pdf';
const PRIVACY_POLICY_URL = 'https://www.cian.ru/legal-documents/politika_konfidencialnosti_0/';

export const FormStep: FC = () => {
  const dispatch = useDispatch<TDispatch>();
  const initialStickerCount = useSelector(selectInitialStickerCount);
  const formStatus = useSelector(selectFormStatus);
  const errorCode = useSelector(selectErrorCode);

  const stickerCountDefault = initialStickerCount ? String(Math.min(initialStickerCount, 5)) : '';

  const {
    register,
    handleSubmit,
    control,
    clearErrors,
    setError,
    setValue,
    formState: { errors, isSubmitting },
    getValues,
  } = useForm<IFormData>({
    mode: 'onBlur',
    defaultValues: {
      postalCode: '',
      selectedAddress: null,
      stickerCount: stickerCountDefault,
    },
  });

  const onSubmit: SubmitHandler<IFormData> = data => {
    dispatch(
      submitSuperhostRequest({
        fullName: data.fullName,
        deliveryAddress: buildDeliveryAddress(data.selectedAddress, data.postalCode),
        stickersCount: Number(data.stickerCount),
      }),
    );
  };

  if (formStatus === 'success') {
    return (
      <section id="form-step" className={style['section']}>
        <div className={style['inner']}>
          <FormResult type="success" />
        </div>
      </section>
    );
  }

  if (formStatus === 'error') {
    return (
      <section id="form-step" className={style['section']}>
        <div className={style['inner']}>
          <FormResult type="error" errorCode={errorCode} />
        </div>
      </section>
    );
  }

  return (
    <section id="form-step" className={style['section']}>
      <div className={style['inner']}>
        <form className={style['card']} onSubmit={handleSubmit(onSubmit)}>
          <div className={style['header']}>
            <span className={style['step-label']}>Шаг 2</span>
            <h2 className={style['title']}>Заполните форму</h2>
          </div>

          <div className={style['field']}>
            <p className={style['label']}>ФИО</p>
            <input
              aria-label="ФИО"
              className={`${style['input']} ${errors.fullName ? style['input-error'] : ''}`.trim()}
              type="text"
              {...register('fullName', { required: 'Укажите ФИО' })}
            />
            {errors.fullName && <span className={style['error']}>{errors.fullName.message}</span>}
          </div>

          <AddressField
            clearErrors={clearErrors}
            control={control}
            errors={errors}
            getValues={getValues}
            register={register}
            setError={setError}
            setValue={setValue}
          />

          <div className={style['field']}>
            <p className={style['label']}>Количество наклеек</p>
            <input
              className={`${style['input']} ${style['input-disabled']}`.trim()}
              type="text"
              disabled
              {...register('stickerCount')}
            />
          </div>

          <label className={`${style['checkbox-row']} ${errors.consent ? style['checkbox-row-error'] : ''}`.trim()}>
            <input
              className={`${style['checkbox']} ${errors.consent ? style['checkbox-error'] : ''}`.trim()}
              type="checkbox"
              {...register('consent', { required: true })}
            />
            <span className={`${style['checkbox-text']} ${errors.consent ? style['checkbox-text-error'] : ''}`.trim()}>
              Нажимая кнопку «Отправить», я даю{' '}
              <a
                className={style['checkbox-link']}
                href={PERSONAL_DATA_CONSENT_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                согласие на обработку моих персональных данных
              </a>{' '}
              в соответствии с{' '}
              <a className={style['checkbox-link']} href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">
                Политикой конфиденциальности
              </a>
              .
            </span>
          </label>

          <Button
            theme="fill_primary"
            size="L"
            fullWidth
            type="submit"
            disabled={isSubmitting || formStatus === 'loading'}
            loading={formStatus === 'loading'}
          >
            Отправить
          </Button>
        </form>
      </div>
    </section>
  );
};
